class FishMobileGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.mobile = matchMedia('(max-width: 749px)');
    this.updateMode = () => this.mobile.matches ? this.connect() : this.disconnect();
    this.mobile.addEventListener('change', this.updateMode);
    this.updateMode();
  }

  connect() {
    if (this.events) return;
    this.events = new AbortController();
    const signal = this.events.signal;
    this.root = this.gallery.closest('[id^="MainProduct-"]');
    this.root?.addEventListener('change', event => {
      const picker = event.target.closest('variant-selects, variant-radios');
      if (!picker?.currentVariant?.featured_media) return;
      this.closeFullscreen();
      this.expectedImage = String(picker.currentVariant.featured_media.id);
      this.pendingScroll = true;
      this.activeId = null;
      this.interacted = false;
      clearTimeout(this.scrollTimer);
      this.sync();
    }, { signal });
    this.gallery.addEventListener('tf:variant-media-ready', () => this.sync(), { signal });
    this.gallery.addEventListener('pointerdown', () => {
      this.touching = true;
      this.interacted = true;
      clearTimeout(this.settleTimer);
    }, { signal, passive: true });
    this.gallery.addEventListener('touchstart', () => {
      this.touchActive = true;
      this.touching = true;
      this.interacted = true;
    }, { signal, passive: true });
    const finishGesture = () => {
      this.touching = this.touchActive;
      this.activateVisible();
      this.scheduleActivation();
    };
    this.gallery.addEventListener('pointerup', finishGesture, { signal, passive: true });
    const finishTouch = () => {
      this.touchActive = false;
      finishGesture();
    };
    this.gallery.addEventListener('touchend', finishTouch, { signal, passive: true });
    this.gallery.addEventListener('touchcancel', finishTouch, { signal, passive: true });
    this.gallery.addEventListener('pointercancel', () => {
      this.touching = this.touchActive;
      this.scheduleActivation();
    }, { signal, passive: true });
    this.gallery.addEventListener('click', event => {
      if (!event.target.closest('[data-target], .slider-button, deferred-media')) return;
      this.interacted = true;
      this.scheduleActivation();
    }, { signal, capture: true });
    this.gallery.addEventListener('scroll', () => this.scheduleActivation(), { signal, capture: true, passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseVideos();
    }, { signal });
    document.addEventListener('fullscreenchange', () => {
      if (this.nativeVideo && !document.fullscreenElement) {
        this.nativeVideo.pause();
        this.pauseVideos();
        this.nativeVideo = null;
      }
    }, { signal });
    this.sync();
  }

  sync() {
    if (!this.events || !this.mobile.matches) return;
    this.viewer = this.gallery.querySelector('.product__media-list.spiceFeat');
    if (!this.viewer) return;
    const first = this.viewer.querySelector('li[data-media-id]');
    if (this.pendingScroll && first?.dataset.mediaId.endsWith(`-${this.expectedImage}`)) {
      this.pendingScroll = false;
      this.viewer.scrollTo({ left: 0, behavior: 'instant' });
      this.activeId = first.dataset.mediaId;
      const expected = this.expectedImage;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        if (!this.mobile.matches || expected !== this.expectedImage || !this.gallery.isConnected) return;
        const header = document.querySelector('sticky-header');
        const headerBox = header?.getBoundingClientRect();
        const offset = headerBox && headerBox.top <= 0 && headerBox.bottom > 0 ? headerBox.bottom : 12;
        window.scrollTo({
          top: Math.max(0, window.scrollY + this.gallery.getBoundingClientRect().top - offset),
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
      }, 150);
    }
  }

  scheduleActivation() {
    clearTimeout(this.settleTimer);
    if (this.touching) return;
    this.settleTimer = setTimeout(() => this.activateVisible(), 140);
  }

  visibleSlide() {
    if (!this.viewer?.isConnected) return null;
    const viewport = this.viewer.getBoundingClientRect();
    if (viewport.bottom <= 0 || viewport.top >= window.innerHeight) return null;
    return Array.from(this.viewer.children).find(item => {
      const box = item.getBoundingClientRect();
      const overlap = Math.max(0, Math.min(box.right, viewport.right) - Math.max(box.left, viewport.left));
      return box.width > 0 && overlap / Math.min(box.width, viewport.width) >= 0.85;
    });
  }

  activateVisible() {
    if (!this.mobile.matches || this.touching || !this.interacted || this.overlay || this.nativeVideo) return;
    const slide = this.visibleSlide();
    if (!slide || this.activeId === slide.dataset.mediaId) return;
    this.activeId = slide.dataset.mediaId;
    this.pauseVideos();
    const media = slide.querySelector('deferred-media');
    const video = media?.prepareVariantVideo?.();
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    media.variantPlaybackSuppressed = false;
    media.loadContent(false);
    this.openFullscreen(media, video);
  }

  pauseVideos() {
    this.gallery.preparedVariantVideos?.forEach(media => {
      media.variantPlayRequested = false;
      media.variantPlaybackSuppressed = true;
      media.variantVideo?.pause();
    });
  }

  openFullscreen(media, video) {
    const currentId = this.activeId;
    const fallback = () => {
      if (this.mobile.matches && this.activeId === currentId && media.isSelectedVariantVideo()) this.openOverlay(media, video);
    };
    if (navigator.userActivation?.isActive) {
      if (typeof video.requestFullscreen === 'function' && document.fullscreenEnabled) {
        try {
          this.nativeVideo = video;
          Promise.resolve(video.requestFullscreen()).catch(() => {
            this.nativeVideo = null;
            fallback();
          });
          return;
        } catch (_) {
          this.nativeVideo = null;
        }
      }
      if (typeof video.webkitEnterFullscreen === 'function' && video.readyState >= 2) {
        try {
          this.nativeVideo = video;
          video.addEventListener('webkitendfullscreen', () => {
            video.pause();
            this.pauseVideos();
            this.nativeVideo = null;
          }, { once: true });
          video.webkitEnterFullscreen();
          return;
        } catch (_) {
          this.nativeVideo = null;
        }
      }
    }
    fallback();
  }

  openOverlay(media, video) {
    if (this.overlay || !video.isConnected) return;
    const dialog = document.createElement('dialog');
    if (typeof dialog.showModal !== 'function') return;
    dialog.dataset.fishVideoOverlay = '';
    dialog.setAttribute('aria-label', this.gallery.getAttribute('aria-label'));
    dialog.style.cssText = 'position:fixed;inset:0;margin:0;padding:0;border:0;width:100vw;max-width:100vw;height:100dvh;max-height:100dvh;background:#000;color:#fff;overflow:hidden;overscroll-behavior:contain;';
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = '×';
    close.setAttribute('aria-label', this.gallery.dataset.closeVideoLabel);
    close.style.cssText = 'position:absolute;z-index:2;top:max(12px,env(safe-area-inset-top));right:max(12px,env(safe-area-inset-right));width:48px;height:48px;border:1px solid #fff;border-radius:50%;background:#111c;color:white;font:32px/1 sans-serif;cursor:pointer;';
    this.overlay = { dialog, media, video, parent: video.parentNode, next: video.nextSibling, style: video.getAttribute('style'), focus: document.activeElement, overflow: document.body.style.overflow, y: window.scrollY };
    video.style.cssText = 'display:block;width:100%;height:100%;max-width:none;max-height:none;object-fit:contain;background:#000;';
    dialog.append(video, close);
    document.body.append(dialog);
    document.body.style.overflow = 'hidden';
    close.addEventListener('click', () => this.closeOverlay());
    dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeOverlay(); });
    let start;
    dialog.addEventListener('touchstart', event => {
      const point = event.touches[0];
      start = point.clientY < window.innerHeight - 80 ? { x: point.clientX, y: point.clientY } : null;
    }, { passive: true });
    dialog.addEventListener('touchend', event => {
      if (!start) return;
      const point = event.changedTouches[0];
      const dx = point.clientX - start.x;
      const dy = point.clientY - start.y;
      start = null;
      if (Math.abs(dx) < 60 || Math.abs(dx) <= Math.abs(dy)) return;
      const slides = Array.from(this.viewer.children);
      const index = slides.findIndex(slide => slide.dataset.mediaId === this.activeId);
      const next = slides[index + (dx > 0 ? -1 : 1)];
      if (!next) return;
      this.closeOverlay();
      this.viewer.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
    }, { passive: true });
    dialog.showModal();
    close.focus({ preventScroll: true });
    media.variantPlayRequested = true;
    media.playVariantVideo();
  }

  closeOverlay() {
    const state = this.overlay;
    if (!state) return;
    this.overlay = null;
    state.media.variantPlayRequested = false;
    state.media.variantPlaybackSuppressed = true;
    state.video.pause();
    if (state.style === null) state.video.removeAttribute('style');
    else state.video.setAttribute('style', state.style);
    if (state.parent.isConnected) state.parent.insertBefore(state.video, state.next?.parentNode === state.parent ? state.next : null);
    state.dialog.close();
    state.dialog.remove();
    document.body.style.overflow = state.overflow;
    window.scrollTo({ top: state.y, behavior: 'instant' });
    if (state.focus?.isConnected) state.focus.focus({ preventScroll: true });
  }

  closeFullscreen() {
    this.closeOverlay();
    if (this.nativeVideo) {
      this.nativeVideo.pause();
      if (document.fullscreenElement === this.nativeVideo) document.exitFullscreen().catch(() => {});
      else if (this.nativeVideo.webkitDisplayingFullscreen) this.nativeVideo.webkitExitFullscreen();
      this.nativeVideo = null;
    }
    this.pauseVideos();
  }

  disconnect() {
    this.closeFullscreen();
    this.events?.abort();
    this.events = null;
    clearTimeout(this.scrollTimer);
    clearTimeout(this.settleTimer);
    this.touching = false;
    this.touchActive = false;
    this.interacted = false;
    this.activeId = null;
  }

  destroy() {
    this.disconnect();
    this.mobile.removeEventListener('change', this.updateMode);
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      this.preparedVariantVideos = new Set();
      this.queueVariantVideo = () => {
        cancelAnimationFrame(this.variantVideoFrame);
        this.variantVideoFrame = requestAnimationFrame(() => this.prepareSelectedVariantVideos());
      };
      this.variantVideoObserver = new MutationObserver(this.queueVariantVideo);
      this.variantVideoObserver.observe(this, { childList: true, subtree: true });
      this.onVariantThumbnailClick = event => {
        const item = event.target.closest('[data-target]');
        if (!item) return;
        this.preparedVariantVideos.forEach(media => {
          media.variantPlaybackSuppressed = media.closest('li[data-media-id]')?.dataset.mediaId !== item.dataset.target;
          if (media.closest('li[data-media-id]')?.dataset.mediaId !== item.dataset.target) {
            media.variantPlayRequested = false;
            media.variantVideo?.pause();
          }
        });
      };
      this.addEventListener('click', this.onVariantThumbnailClick, true);
      this.fishMobileGallery = new FishMobileGallery(this);
      this.queueVariantVideo();
    }

    disconnectedCallback() {
      this.variantVideoObserver?.disconnect();
      this.fishMobileGallery?.destroy();
      cancelAnimationFrame(this.variantVideoFrame);
      this.removeEventListener('click', this.onVariantThumbnailClick, true);
      this.preparedVariantVideos?.forEach(media => media.releaseVariantVideo?.());
    }

    prepareSelectedVariantVideos() {
      if (!this.isConnected) return;
      const group = this.querySelector('.spiceThumb');
      if (!group) return;
      const viewer = this.querySelector('.product__media-list.spiceFeat') || this.querySelector('.product__media-list');
      if (!viewer) return;
      const selected = new Set();
      group.querySelectorAll('[data-target]').forEach(thumbnail => {
        const item = Array.from(viewer.children).find(media => media.dataset.mediaId === thumbnail.dataset.target);
        const deferred = item?.querySelector('deferred-media');
        if (deferred?.querySelector('template')?.content.querySelector('video')) selected.add(deferred);
      });
      this.preparedVariantVideos.forEach(media => {
        if (!selected.has(media)) media.releaseVariantVideo?.();
      });
      this.preparedVariantVideos = selected;
      selected.forEach(media => media.prepareVariantVideo?.());
      this.dispatchEvent(new CustomEvent('tf:variant-media-ready'));
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
